import { getLogger } from '@logtape/logtape';
import { v4 as uuidv4 } from 'uuid';

import { DailyAppointment } from '@/models/appointment/Appointment.ts';

import { useFilterStore } from '@/stores/filterStore.ts';

const logger = getLogger(['util', 'testing']);

/**
 * Multiplies appointments for testing purposes by creating duplicates with unique IDs
 * Each duplicate gets a slightly different start/end time to avoid exact overlaps
 */
export const multiplyAppointmentsForTesting = (appointments: DailyAppointment[]): DailyAppointment[] => {
    const multiplier = useFilterStore.getState().appointment_multiplier;

    // If multiplier is 1 or less, return original appointments
    if (multiplier <= 1) {
        return appointments;
    }

    const multiplied_appointments: DailyAppointment[] = [];

    for (const appointment of appointments) {
        // Add original appointment
        multiplied_appointments.push(appointment);

        // Create duplicates
        for (let i = 1; i < multiplier; i++) {
            // Generate random time within business hours (8 AM to 6 PM)
            const random_hour = Math.floor(Math.random() * 10) + 8; // 8-17 (8 AM to 5 PM)
            const random_minute = Math.floor(Math.random() * 60); // 0-59 minutes

            // Get the original appointment date
            const original_date = new Date(appointment.start);

            // Create new random time on the same date
            const new_start_time = new Date(original_date);
            new_start_time.setHours(random_hour, random_minute, 0, 0);

            // Calculate duration and set end time
            const original_duration = new Date(appointment.end).getTime() - new Date(appointment.start).getTime();
            const new_end_time = new Date(new_start_time.getTime() + original_duration);

            const duplicate_appointment: DailyAppointment = {
                ...appointment,
                id: `${appointment.id}_duplicate_${i}_${uuidv4().slice(0, 8)}`,
                start: new_start_time.toISOString(),
                end: new_end_time.toISOString(),
            };

            multiplied_appointments.push(duplicate_appointment);
        }
    }

    logger.warn(
        '🧪 Testing: Multiplied {sourceCount} appointments by {multiplier}x to {totalCount} total appointments',
        {
            sourceCount: appointments.length,
            multiplier: multiplier,
            totalCount: multiplied_appointments.length,
        }
    );

    return multiplied_appointments;
};
